// src/pages/gene.codes/GeneticWheel/dashboardPanels/DetailPanel.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tabs, Collapse, Tag, Alert, Typography } from "antd";
import { ContrastTag } from "../../../../components/shared";
import {
	HeartOutlined,
	ExperimentOutlined,
	ExclamationCircleOutlined,
	EnvironmentOutlined,
	CompassOutlined,
	ColumnWidthOutlined,
	BarChartOutlined,
} from "@ant-design/icons";

import ChromosomeMap from "../ChromosomeMap/ChromosomeMap";
import OrganExpressionVisualization from "../OrganExpression/OrganExpressionVisualization";

import { Panel as PanelType } from "../../../../types/geneCodes/Panel";
import { Trait } from "../../../../types/geneCodes/TraitTypes";
import { Staging } from "../../../../types/geneCodes/StagingTypes";
import { defaultRefSeq2, RefSeq2 } from "../../../../types/geneCodes/RefSeq2Types";
import { Chromosome, defaultChromosome } from "../../../../types/geneCodes/ChromosomeTypes";

import { getChromosomeByGene } from "../../../../api/geneCodes/ChromosomeApi";
import { getRefSeq2ByGene } from "../../../../api/geneCodes/RefSeq2Api";
import { AiPanelData } from "../../../../types/geneCodes/AiPanelDataTypes";
import { GeneStage } from "../../../../types/geneCodes/GeneStageTypes";
import { processGeneticResults, UNGENOTYPED } from "../../../../utils/geneCodes/geneticAnalysisUtils";
import { lookupVariant } from "../../../../utils/geneCodes/variantEvidence";
import { RegistryVariantLookup } from "../../../../types/geneCodes/RegistryTypes";
import { apoeIsotype } from "../../../../utils/geneCodes/apoeIsotype";
import ProteinStructurePanel from "./ProteinStructurePanel";
import { fetchGeneOntology } from "../../../../api/geneCodes/GeneExpressionApi";
import { GeneOntology } from "../../../../types/geneCodes/GeneExpressionTypes";
import { InlineLoading, EmptyState, ErrorBoundary } from "../../../../components/shared";
import SnpEvidencePanel from "./SnpEvidencePanel";
import PanelReport from "./PanelReport/PanelReport";
import { AiPanelReport } from "../../../../types/geneCodes/AiPanelReportTypes";
import { legacyReportToHtml } from "./PanelReport/htmlSanitizer";
import { normalizeReport } from "./PanelReport/normalizeReport";
import "./dashboardPanels.css";

const { Text } = Typography;

const DetailFallback: React.FC = () => (
	<div className="mg-detail-panel__fallback">
		<Alert
			type="warning"
			showIcon
			message="This section could not be displayed"
			description="Select another segment in the wheel, or refresh the page."
		/>
	</div>
);

interface DetailPanelProps {
	selectedPanel: PanelType | null;
	selectedTrait: Trait | null;
	selectedGene: string | null;
	selectedSnp: Staging | null;
	snps: Staging[];
	loading?: boolean;
	activeIndex: number;
	setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
	aiPanelData: AiPanelData | null;
	geneticData?: GeneStage[];
}

const DetailPanel: React.FC<DetailPanelProps> = ({ selectedPanel, selectedTrait, selectedGene, selectedSnp, snps, loading = false, activeIndex = 0, setActiveIndex, aiPanelData, geneticData }) => {
	const [refseq, setRefseq] = useState<RefSeq2>(defaultRefSeq2);
	const [chromosome, setChromosome] = useState<Chromosome>(defaultChromosome);
	const [structuredData, setStructuredData] = useState<AiPanelReport | null>(null);
	const [isStructuredFormat, setIsStructuredFormat] = useState(false);
	const [detailTabKey, setDetailTabKey] = useState("0");
	const [snpSectionKeys, setSnpSectionKeys] = useState<string[]>([]);

	const getActiveTabIndex = () => {
		let tabIndex = 0;
		if (selectedSnp) {
			if (selectedPanel) tabIndex++;
			if (selectedTrait) tabIndex++;
			if (selectedGene) tabIndex++;
			return tabIndex.toString();
		}
		if (selectedGene) {
			if (selectedPanel) tabIndex++;
			if (selectedTrait) tabIndex++;
			return tabIndex.toString();
		}
		if (selectedTrait) {
			if (selectedPanel) tabIndex++;
			return tabIndex.toString();
		}
		return "0";
	};

	useEffect(() => {
		if (aiPanelData?.description) {
			try {
				const parsed = JSON.parse(aiPanelData.description);
				setStructuredData(normalizeReport(parsed));
				setIsStructuredFormat(true);
			} catch {
				setIsStructuredFormat(false);
				setStructuredData(null);
			}
			return;
		}
		// Without this branch the previous panel's report stays on screen under the new panel's label.
		setStructuredData(null);
		setIsStructuredFormat(false);
	}, [aiPanelData]);

	useEffect(() => {
		// Reset before the fetch resolves so gene A's chromosome can never render under gene B.
		setChromosome(defaultChromosome);
		setRefseq(defaultRefSeq2);

		if (!selectedGene) return;

		let cancelled = false;
		const loadGeneticInfo = async () => {
			try {
				const { data: chrom } = await getChromosomeByGene(selectedGene, 1, 1);
				const { data: ref } = await getRefSeq2ByGene(selectedGene, 1, 1);
				if (cancelled) return;
				// Both endpoints return 200 with an empty array for an unknown gene — never 404.
				setChromosome(chrom?.[0] ?? defaultChromosome);
				setRefseq(ref?.[0] ?? defaultRefSeq2);
			} catch (err) {
				if (cancelled) return;
				setChromosome(defaultChromosome);
				setRefseq(defaultRefSeq2);
				console.error("gene_codes.detail_panel.reference_fetch.failed", { gene: selectedGene, err });
			}
		};
		loadGeneticInfo();

		return () => {
			cancelled = true;
		};
	}, [selectedGene]);

	useEffect(() => {
		const newTabKey = getActiveTabIndex();
		setDetailTabKey(newTabKey);
	}, [selectedPanel, selectedTrait, selectedGene, selectedSnp]);

	// Research Evidence re-collapses on every SNP change, so drilling the wheel never costs a literature search.
	useEffect(() => {
		setSnpSectionKeys([]);
	}, [selectedSnp]);

	// Same map the sunburst colours its arcs from, so the sidebar and the wheel can never disagree.
	const genotypeResults = useMemo(() => processGeneticResults(geneticData ?? [], snps), [geneticData, snps]);

	const selectedGenotype = selectedSnp?.rs ? genotypeResults.get(selectedSnp.rs.toLowerCase()) : undefined;
	const notGenotyped = selectedGenotype?.riskStatus === UNGENOTYPED;

	// Only asked for when there is nothing to show — on_array is what separates "the array cannot
	// measure this" from "this sample's import missed it", and they lead to opposite actions.
	const { data: arrayLookup } = useQuery<RegistryVariantLookup>({
		queryKey: ["registry-variant", selectedSnp?.rs?.toLowerCase()],
		queryFn: () => lookupVariant(selectedSnp?.rs as string),
		enabled: notGenotyped && Boolean(selectedSnp?.rs),
		staleTime: 1000 * 60 * 60,
	});

	// Same queryKey as GeneOntologyPanel, so the "encodes" line costs no extra request.
	const { data: geneOntology } = useQuery<GeneOntology>({
		queryKey: ["gene-ontology", selectedGene],
		queryFn: () => fetchGeneOntology(selectedGene as string),
		enabled: !!selectedGene,
		staleTime: 1000 * 60 * 60,
	});

	if (loading) {
		return <InlineLoading message="Loading panel data…" />;
	}

	// 3060 of 3154 `panel_ai_data` rows predate the JSON payload. This path renders them, and
	// it now SANITISES first — the content is model output and was previously injected raw.
	const renderLegacyPanelData = () => {
		const descriptionContent = aiPanelData?.description || selectedPanel?.desc_long || selectedPanel?.desc_short || "No description available.";

		return <div className="mg-prose mg-prose-scroll" dangerouslySetInnerHTML={{ __html: legacyReportToHtml(descriptionContent) }} />;
	};

	const renderPanelDetails = () => {
		if (!selectedPanel) return null;
		// ⚠️ The structured report is NOT wrapped in .mg-detail-scroll. index.css:800 forces
		// `.ant-tabs-content-holder { overflow: scroll !important }` app-wide, so the tab holder is
		// the real scroller and .mg-detail-scroll (height:100% of an auto-height pane) never scrolls
		// — it only sits between the report's sticky nav and its scrollport, so the nav never sticks.
		if (isStructuredFormat && structuredData) {
			return <PanelReport data={structuredData} generatedAt={aiPanelData?.date} />;
		}
		return <div className="mg-detail-scroll mg-detail-scroll--wide">{renderLegacyPanelData()}</div>;
	};

	const renderTraitDetails = () => {
		if (!selectedTrait) return null;

		// Read straight off geneticData, not genotypeResults — the two APOE SNPs need not be in this panel's staging set.
		const alleles = (rsId: string): [string, string] | null => {
			const row = (geneticData ?? []).find((g) => g.rs_stage?.toLowerCase() === rsId);
			return row ? [row.a1_stage, row.a2_stage] : null;
		};
		const rs429358 = alleles("rs429358");
		const rs7412 = alleles("rs7412");
		const isotype =
			selectedTrait.trait_name === "late onset AD" && rs429358 && rs7412 ? apoeIsotype(rs429358, rs7412) : null;

		return (
			<div className="mg-detail-scroll">
				<div className="mg-detail-stack">
					{isotype && (
						<div className="mg-detail-callout mg-detail-callout--info" data-testid="trait-apoe-isotype">
							<Text className="mg-detail-callout__title">APOE isotype</Text>
							<div className="mg-detail-callout__row">
								<Tag color="blue">{isotype.isotype}</Tag>
								<Text className="mg-detail-callout__body">{isotype.note}</Text>
							</div>
						</div>
					)}

					{selectedTrait.trait_desc && (
						<div className="mg-detail-trait-desc">
							<div dangerouslySetInnerHTML={{ __html: selectedTrait.trait_desc }} />
						</div>
					)}

					<div className="mg-detail-grid">
						{selectedTrait.lifetime_risk && (
							<div className="mg-detail-callout mg-detail-callout--warning mg-detail-callout--inline">
								<ExclamationCircleOutlined className="mg-detail-callout__icon mg-detail-callout__icon--warning" />
								<div>
									<Text className="mg-detail-callout__title">Lifetime risk</Text>
									<Text className="mg-detail-callout__value mg-detail-callout__value--warning">{selectedTrait.lifetime_risk}</Text>
								</div>
							</div>
						)}

						{selectedTrait.trait_treatment && (
							<div className="mg-detail-callout mg-detail-callout--success">
								<div className="mg-detail-callout__head">
									<HeartOutlined className="mg-detail-callout__icon mg-detail-callout__icon--success" />
									<Text className="mg-detail-callout__title">Treatment information</Text>
								</div>
								<Text className="mg-detail-callout__body">{selectedTrait.trait_treatment}</Text>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	};

	const renderGeneDetails = () => {
		if (!selectedGene) return null;

		const formatChromosomeNumber = (chr: string | null) => {
			if (!chr) return null;
			let formatted = chr.toString().replace(/^chr/i, "").trim();
			if (formatted === "23") return "X";
			if (formatted === "24") return "Y";
			return formatted;
		};

		const chrNumber = formatChromosomeNumber(refseq.chrom || chromosome.Chromosome_name);
		const showChromosomeMap = chrNumber && chromosome.Gene_start && chromosome.Gene_end;

		const collapseItems = [
			{
				key: "1",
				label: <span className="mg-detail-section-label">Gene overview</span>,
				children: (
					<div className="mg-detail-overview">
						<Text className="mg-detail-overview__lead" data-testid="gene-encodes">
							{geneOntology?.gene_full_name ? `${selectedGene} encodes ${geneOntology.gene_full_name}` : selectedGene}
						</Text>

						{refseq.refseq_desc && (
							<div>
								<Text className="mg-detail-overview__label">Description</Text>
								<Text className="mg-detail-overview__prose">{refseq.refseq_desc}</Text>
							</div>
						)}

						<div className="mg-detail-facts">
							{chrNumber && (
								<div className="mg-detail-fact">
									<EnvironmentOutlined className="mg-detail-fact__icon" />
									<div>
										<Text className="mg-detail-fact__label">Chromosome</Text>
										<Text className="mg-detail-fact__value">{chrNumber}</Text>
									</div>
								</div>
							)}

							{refseq.chrom_map && (
								<div className="mg-detail-fact mg-detail-fact--success">
									<CompassOutlined className="mg-detail-fact__icon" />
									<div>
										<Text className="mg-detail-fact__label">Position</Text>
										<Text className="mg-detail-fact__value">{refseq.chrom_map}</Text>
									</div>
								</div>
							)}

							{refseq.gene_size && (
								<div className="mg-detail-fact mg-detail-fact--warning">
									<ColumnWidthOutlined className="mg-detail-fact__icon" />
									<div>
										<Text className="mg-detail-fact__label">Gene size</Text>
										<Text className="mg-detail-fact__value">{refseq.gene_size} bp</Text>
									</div>
								</div>
							)}

							{chromosome.Gene_GC && (
								<div className="mg-detail-fact mg-detail-fact--primary">
									<BarChartOutlined className="mg-detail-fact__icon" />
									<div>
										<Text className="mg-detail-fact__label">GC content</Text>
										<Text className="mg-detail-fact__value">{chromosome.Gene_GC}%</Text>
									</div>
								</div>
							)}
						</div>
					</div>
				),
			},
			showChromosomeMap && {
				key: "2",
				label: <span className="mg-detail-section-label">Chromosome map</span>,
				children: (
					<div className="mg-detail-section-body">
						<ChromosomeMap geneName={selectedGene} chromosomeNumber={chrNumber} geneStart={chromosome.Gene_start} geneEnd={chromosome.Gene_end} height={300} orientation="horizontal" />
					</div>
				),
			},
			{
				key: "3",
				label: (
					<span className="mg-detail-section-label">
						<HeartOutlined className="mg-detail-section-label__icon--danger" />
						Expression in organs
					</span>
				),
				children: (
					<div className="mg-detail-section-body">
						<OrganExpressionVisualization geneName={selectedGene} />
					</div>
				),
			},
			{
				key: "structure",
				label: (
					<span className="mg-detail-section-label">
						<ExperimentOutlined className="mg-detail-section-label__icon--primary" />
						3D protein structure
					</span>
				),
				children: (
					<div className="mg-detail-section-body">
						<ProteinStructurePanel geneName={selectedGene} />
					</div>
				),
			},
		].filter(Boolean) as Array<{ key: string; label: React.ReactElement; children: React.ReactElement }>;

		return (
			<div className="mg-detail-scroll">
				<Collapse items={collapseItems} defaultActiveKey={["1"]} size="small" />
			</div>
		);
	};

	const renderSnpDetails = () => {
		if (!selectedSnp) return null;

		const genotype = selectedGenotype;
		const zygosity = (() => {
			// Never genotyped is NOT a result — it must not fall through to the no-call neutral.
			if (notGenotyped) return { label: "Not genotyped for this sample", color: "var(--mg-info)" };
			if (!genotype || genotype.riskStatus === null) return { label: "Not called for this sample", color: "var(--mg-border)" };
			if (genotype.riskStatus === "bad") return { label: "Homozygous risk", color: "var(--mg-danger)" };
			if (genotype.riskStatus === "medium") return { label: "Heterozygous", color: "var(--mg-warning)" };
			return { label: "No risk allele", color: "var(--mg-success)" };
		})();

		const onArray = arrayLookup?.status === "found" ? arrayLookup.doc.on_array : null;
		const notGenotypedReason =
			onArray === false
				? "Not on the array — the clinic's genotyping array cannot measure this variant, so no sample can carry a result for it."
				: onArray === true
					? "On the array, but absent from this sample's import — a genestage backfill can recover it."
					: "Absent from this sample's import. The registry cannot say whether the array measures this variant.";

		return (
			<div className="mg-detail-snp">
				{/* Flat surface rather than a nested antd Card — design-system §10/§24. */}
				<div className="mg-detail-result" style={{ "--mg-zygosity": zygosity.color } as React.CSSProperties} data-testid="snp-your-result">
					<div className="mg-detail-result__head">Result</div>
					<div className="mg-detail-result__body">
						<div className="mg-detail-result__row">
							{genotype && genotype.riskStatus !== null && !notGenotyped ? (
								<ContrastTag background={zygosity.color}>
									{genotype.allele1} {genotype.allele2}
								</ContrastTag>
							) : (
								<Tag>{notGenotyped ? "Not genotyped" : "Not called"}</Tag>
							)}
							<span className="mg-detail-result__zygosity">{zygosity.label}</span>
						</div>
						{notGenotyped && (
							<div className="mg-detail-result__ungenotyped" data-testid="snp-not-genotyped" data-on-array={onArray === null ? "unknown" : String(onArray)}>
								{notGenotypedReason}
							</div>
						)}
						{selectedSnp.risk_allele && (
							<div className="mg-detail-result__allele">
								Risk allele for this SNP: <strong>{selectedSnp.risk_allele}</strong>
								{/* {selectedSnp.bene_allele ? ` · beneficial allele: ${selectedSnp.bene_allele}` : ""} */}
							</div>
						)}
					</div>
				</div>

				<div className="mg-detail-evidence">
					<SnpEvidencePanel rs={selectedSnp.rs} searcher={selectedSnp.searcher} />
				</div>
			</div>
		);
	};

	const handleDetailTabChange = (key: string) => {
		setDetailTabKey(key);
	};

	const tabItems = [
		selectedPanel && {
			key: "0",
			label: <span >{selectedPanel.name_panel}</span>,
			children: renderPanelDetails(),
		},
		selectedTrait && {
			key: (selectedPanel ? 1 : 0).toString(),
			label: <span >{selectedTrait.trait_name}</span>,
			children: renderTraitDetails(),
		},
		selectedGene && {
			key: ((selectedPanel ? 1 : 0) + (selectedTrait ? 1 : 0)).toString(),
			label: <span >{selectedGene}</span>,
			children: renderGeneDetails(),
		},
		selectedSnp && {
			key: ((selectedPanel ? 1 : 0) + (selectedTrait ? 1 : 0) + (selectedGene ? 1 : 0)).toString(),
			label: <span >{selectedSnp.rs}</span>,
			children: renderSnpDetails(),
		},
	].filter(Boolean) as Array<{ key: string; label: React.ReactElement; children: React.ReactNode }>;

	return (
		<div className="mg-detail-panel">
			{!selectedPanel && !selectedTrait && !selectedGene && !selectedSnp ? (
				<div className="mg-detail-panel__empty">
					<EmptyState
						title="Select a segment to view details"
						description="Click on a segment in the wheel to view detailed genetic information"
					/>
				</div>
			) : (
				// Kept inside DetailPanel so both call sites (WheelModal, WheelDashboard) inherit it.
				<ErrorBoundary key={detailTabKey} fallback={<DetailFallback />}>
					<Tabs activeKey={detailTabKey} onChange={handleDetailTabChange} items={tabItems} size="small" className="mg-detail-panel__tabs" />
				</ErrorBoundary>
			)}
		</div>
	);
};

export default DetailPanel;
